<script lang="ts">
	import { DashLightControlWidget, Icon, Text } from '@Shelly/shelly-design-system/index';
	import ControlGroupGainBrightness from 'src/components/Controls/Group/ControlGroupGainBrightness.svelte';
	import {
		createMemberSettingsSnapshot,
		createMemberStatusSnapshot,
	} from 'src/stores/device-runtime/status-selectors';
	import { devices } from 'src/stores/lists/devices';
	import { groups } from 'src/stores/lists/groups';
	import type { Device } from 'src/types/Device';
	import {
		mapLightGroupDevices,
		getLightCategoryDevices,
		getGroupDeviceState,
		DASH_LIGHT_WIDGET_BG,
	} from '../../dash-lights-helpers';
	import { checkForMissingDevice } from 'src/components/Lists/list-helpers';
	import { load_image_from, real_id } from 'src/helpers/interface_helpers';
	import { getOfflineDeviceCount } from 'src/stores/device-runtime/states';
	import { format } from 'svelte-i18n';
	import { edit } from 'src/stores/ui/edit_lists';
	import { sidePanelStateCombined } from 'src/stores/ui/sidepanel-public';
	import ControlGroupOnOff from 'src/components/Controls/Group/ControlGroupOnOff.svelte';

	export let lightWidget;

	let brightnessValue = 1;

	$: group = $groups?.[lightWidget?.id];

	$: memberStatusStore = createMemberStatusSnapshot(group?.devices ?? []);
	$: memberSettingsStore = createMemberSettingsSnapshot(group?.devices ?? []);
	$: memberStatus = $memberStatusStore;
	$: memberSettings = $memberSettingsStore;

	$: list = mapLightGroupDevices(group?.devices);
	$: lights_category_devices = getLightCategoryDevices(
		list as Device[],
		memberStatus,
		memberSettings,
		$devices,
	);
	$: hasMissingDevice = !!checkForMissingDevice(group?.devices)?.length;

	$: groupDeviceStatus = group?.devices
		.filter((id) => typeof $devices[id] !== 'undefined')
		.map((id) => {
			const state = getGroupDeviceState(group.type, $devices[id], memberStatus[real_id(id)]);
			return state;
		});

	$: devicesOn = groupDeviceStatus?.filter((status) => status)?.length;

	$: devicesOff = groupDeviceStatus?.filter((status) => status === false)?.length;

	$: offlineCountStore = getOfflineDeviceCount(group?.devices);
	$: offlineDevices = $offlineCountStore;

	$: additionalText = !group
		? {
				value: $format('label_missing_group', {
					default: 'Missing Group',
				}),
			}
		: {
				value: `${devicesOn} on, ${devicesOff} off, ${offlineDevices} offline`,
				icon: '',
			};

	$: labelProps = hasMissingDevice
		? {
				value: $format('label_missing_device', {
					default: 'Missing Device',
				}),
				icon: 'fal fa-triangle-exclamation',
				bgOpacity: true,
				iconColor: '--color-warning',
				textClass: 'text-warning',
			}
		: undefined;
</script>

<DashLightControlWidget
	class="flex-1 min-width-0"
	isFocusable
	backgroundColor={lightWidget.bg || DASH_LIGHT_WIDGET_BG[0]}
	title={!group ? lightWidget?.id : group?.name}
	avatar={!group
		? { icon: 'fat fa-triangle-exclamation text-color-warning' }
		: {
				backgroundColor: group?.backgroundColor || group?._meta?.backgroundColor,
				image: group?.image ? load_image_from(group?.image) : '',
			}}
	additionalText={!hasMissingDevice ? additionalText : undefined}
	{labelProps}
	on:click
	on:click-top-right-btn
	on:click-bottom-right-btn
	selected={lightWidget?.id === $sidePanelStateCombined?.id}
	hideRightTitleSlot={!group}
	hideSlotMain={!group}
	hideSlotActions={!group || $edit}
>
	<ControlGroupGainBrightness
		slot="main"
		type="horizontal"
		dashControlWidget={true}
		{lights_category_devices}
		groupId={lightWidget?.id}
		bind:value={brightnessValue}
	/>

	<div class="d-flex gap-xs" slot="rightTitle">
		<Icon size="xs" icon="fal fa-sun-bright" />
		<Text noWrap styleType="body-extra-small">{brightnessValue}%</Text>
	</div>

	<ControlGroupOnOff {group} variant="dashLightControl" slot="actions" />
</DashLightControlWidget>
